import { Activity, AreaChart, BarChart3, PieChart } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import api from "../../services/api";

const AnalyticsPanel = () => {
  const [analytics, setAnalytics] = useState({
    issuesByCategory: [],
    issuesByState: [],
    issuesOverTime: [],
    userGrowth: [],
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("7d");

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/admin/analytics?range=${timeRange}`);
        setAnalytics({
          issuesByCategory: res.data?.issuesByCategory || [],
          issuesByState: res.data?.issuesByState || [],
          issuesOverTime: res.data?.issuesOverTime || [],
          userGrowth: res.data?.userGrowth || [],
        });
      } catch (err) {
        console.error("Error fetching analytics:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [timeRange]);

  const categoryColors = {
    Road: "#ff9933",
    Water: "#2f6fed",
    Electricity: "#f2b134",
    Women: "#f26b5b",
    Unemployment: "#7c63ff",
    School: "#14b8a6",
    Police: "#d95d39",
    Health: "#138808",
    Admin: "#475569",
    Other: "#9ca3af",
  };

  const renderEmptyState = (title) => (
    <div className="rounded-[1.75rem] border border-[#d7dfd4] bg-white p-8 text-center text-sm text-slate-500 shadow-sm">
      {title} data is not available for this range yet.
    </div>
  );

  const renderBarChart = (data, title, valueKey = "value") => {
    if (!data.length) return renderEmptyState(title);
    const maxValue = Math.max(...data.map((item) => item[valueKey] || 0), 1);

    return (
      <div className="rounded-[1.75rem] border border-[#d7dfd4] bg-white p-6 shadow-sm">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#edf8ef] text-[#138808]">
            <BarChart3 className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
            <p className="text-sm text-slate-500">Current leaderboard snapshot</p>
          </div>
        </div>
        <div className="space-y-4">
          {data.map((item, index) => (
            <motion.div
              key={`${item.name || item._id}-${index}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className="space-y-2"
            >
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-slate-700">{item.name || item._id || "Unknown"}</span>
                <span className="text-slate-500">{(item[valueKey] || 0).toLocaleString()}</span>
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-slate-100">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${((item[valueKey] || 0) / maxValue) * 100}%` }}
                  transition={{ delay: index * 0.05 + 0.1, duration: 0.45 }}
                  className="h-full rounded-full"
                  style={{ backgroundColor: categoryColors[item.name] || "#ff9933" }}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    );
  };

  const renderLineChart = (data, title) => {
    if (!data.length) return renderEmptyState(title);

    const maxValue = Math.max(...data.map((item) => item.value || 0), 1);
    const points = data
      .map((item, index) => {
        const x = (index / Math.max(data.length - 1, 1)) * 100;
        const y = 100 - ((item.value || 0) / maxValue) * 78;
        return `${x},${y}`;
      })
      .join(" ");

    return (
      <div className="rounded-[1.75rem] border border-[#d7dfd4] bg-white p-6 shadow-sm">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#fff1df] text-[#b76500]">
            <AreaChart className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
            <p className="text-sm text-slate-500">Trend line across the selected range</p>
          </div>
        </div>
        <div className="relative h-56">
          <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <linearGradient id={`lineGradient-${title}`} x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#ff9933" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#ff9933" stopOpacity="0" />
              </linearGradient>
            </defs>
            <polygon points={`0,100 ${points} 100,100`} fill={`url(#lineGradient-${title})`} />
            <polyline points={points} fill="none" stroke="#138808" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div className="absolute bottom-0 left-0 right-0 flex justify-between text-[11px] text-slate-400">
            {data.map((item, index) => (
              <span key={`${item.date}-${index}`}>{item.date || "N/A"}</span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderPieChart = (data, title) => {
    if (!data.length) return renderEmptyState(title);

    const total = data.reduce((sum, item) => sum + (item.value || 0), 0) || 1;
    let currentAngle = 0;

    return (
      <div className="rounded-[1.75rem] border border-[#d7dfd4] bg-white p-6 shadow-sm">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#fff0ec] text-[#d95d39]">
            <PieChart className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
            <p className="text-sm text-slate-500">Distribution by category</p>
          </div>
        </div>
        <div className="flex flex-col gap-6 xl:flex-row xl:items-center">
          <div className="relative h-44 w-44">
            <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90 transform">
              {data.map((item, index) => {
                const percentage = ((item.value || 0) / total) * 100;
                const angle = (percentage / 100) * 360;
                const startAngle = currentAngle;
                const endAngle = currentAngle + angle;
                const x1 = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
                const y1 = 50 + 40 * Math.sin((startAngle * Math.PI) / 180);
                const x2 = 50 + 40 * Math.cos((endAngle * Math.PI) / 180);
                const y2 = 50 + 40 * Math.sin((endAngle * Math.PI) / 180);
                const largeArcFlag = angle > 180 ? 1 : 0;

                currentAngle += angle;

                return (
                  <path
                    key={`${item.name}-${index}`}
                    d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                    fill={categoryColors[item.name] || "#ff9933"}
                  />
                );
              })}
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-center">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Total</p>
                <p className="text-2xl font-semibold text-slate-900">{total.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="flex-1 space-y-2">
            {data.map((item, index) => (
              <div key={`${item.name}-${index}`} className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3 text-sm">
                <div className="flex items-center gap-3">
                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: categoryColors[item.name] || "#ff9933" }} />
                  <span className="font-medium text-slate-700">{item.name}</span>
                </div>
                <span className="text-slate-500">
                  {(item.value || 0).toLocaleString()} ({(((item.value || 0) / total) * 100).toFixed(1)}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#ff9933]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Analytics</h2>
          <p className="text-sm text-slate-500">Live backend metrics for issue activity and user growth.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {["7d", "30d", "90d", "1y"].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`rounded-2xl px-4 py-2.5 text-sm font-medium transition-colors ${
                timeRange === range
                  ? "bg-slate-900 text-white"
                  : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {range === "7d" ? "7 Days" : range === "30d" ? "30 Days" : range === "90d" ? "90 Days" : "1 Year"}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-[1.75rem] border border-[#d7dfd4] bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#fff1df] text-[#b76500]">
              <Activity className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Category buckets</p>
              <p className="text-2xl font-semibold text-slate-900">{analytics.issuesByCategory.length.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="rounded-[1.75rem] border border-[#d7dfd4] bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#edf8ef] text-[#138808]">
              <BarChart3 className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Tracked states</p>
              <p className="text-2xl font-semibold text-slate-900">{analytics.issuesByState.length.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="rounded-[1.75rem] border border-[#d7dfd4] bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#eef4ff] text-[#2f6fed]">
              <AreaChart className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Time points</p>
              <p className="text-2xl font-semibold text-slate-900">{analytics.issuesOverTime.length.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {renderPieChart(analytics.issuesByCategory, "Issues by Category")}
        {renderBarChart(analytics.issuesByState, "Top States by Issues", "count")}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {renderLineChart(analytics.issuesOverTime, "Issues Over Time")}
        {renderLineChart(analytics.userGrowth, "User Growth")}
      </div>
    </div>
  );
};

export default AnalyticsPanel;
